/**
 * Admin Q&A panel — "Ask your workspace". Sends a natural-language
 * question to the Org Brain and shows:
 *   - a plain-language ANSWER card (LLM-synthesized, grounded in config),
 *   - the exact settings it was based on (collapsible citations),
 *   - example-question chips to get started,
 *   - clear empty / low-confidence / error states.
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { AskResponse, useAskOrgBrain } from './hooks';
import OrgBrainGraph from './OrgBrainGraph';

interface Props {
  tenantId: string;
}

const EXAMPLES = [
  'What happens when a high-priority VIP ticket arrives?',
  'Which automation rules run when a ticket is created?',
  'What ticket types exist and which pipeline does each use?',
  'What is the response target for the urgent SLA?',
];

export default function OrgBrainAsk({ tenantId }: Props) {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<AskResponse | null>(null);
  const [showSources, setShowSources] = useState(false);
  const ask = useAskOrgBrain(tenantId);

  async function runAsk(q: string) {
    const question = q.trim();
    if (!question) return;
    setShowSources(false);
    const r = await ask.mutateAsync({ query: question });
    setResponse(r);
  }

  const hasMatches = (response?.matches?.length ?? 0) > 0;

  return (
    <Card className="p-6 space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Ask your workspace</h2>
        <p className="text-sm text-gray-600 mt-1">
          Ask a question in plain English about your workflows, rules,
          pipelines, or help articles. Each answer shows the exact settings it
          was based on.
        </p>
      </header>

      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              runAsk(query);
            }
          }}
          placeholder="e.g. What happens when a high-priority VIP ticket arrives?"
          className="flex-1 border rounded px-3 py-2"
          aria-label="Ask your workspace a question"
        />
        <Button onClick={() => runAsk(query)} disabled={!query.trim() || ask.isPending}>
          {ask.isPending ? 'Asking…' : 'Ask'}
        </Button>
      </div>

      {/* Example chips — shown until the first answer to help admins start. */}
      {!response && !ask.isPending && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => {
                setQuery(ex);
                runAsk(ex);
              }}
              className="text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {ask.isPending && <Spinner />}

      {response && !ask.isPending && (
        <div className="space-y-3">
          {/* Answer card */}
          {response.answer ? (
            <div
              className={`rounded-lg p-4 border ${
                response.grounded
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                {response.grounded ? 'Answer' : 'No confident match'}
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
                {response.answer}
              </div>
            </div>
          ) : hasMatches ? null : (
            <div className="rounded-lg p-4 bg-gray-50 border border-gray-200 text-sm text-gray-700">
              No matching configuration found. If you just enabled Org Brain,
              index your workspace from the settings above, then try again.
            </div>
          )}

          {/* Knowledge-graph view of the grounding — nodes + relationships. */}
          {(response.graph?.nodes?.length ?? 0) > 0 && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                How this connects
              </div>
              <div className="border border-gray-200 rounded-lg p-2 bg-white">
                <OrgBrainGraph
                  graph={response.graph!}
                  citedIds={response.cited_node_ids}
                />
              </div>
            </div>
          )}

          {/* Citations — collapsed by default; this is the grounding. */}
          {hasMatches && (
            <div>
              <button
                type="button"
                onClick={() => setShowSources((s) => !s)}
                className="text-xs text-primary-700 hover:underline"
              >
                {showSources ? 'Hide' : 'Show'} what this answer is based on (
                {response.matches.length}{' '}
                {response.matches.length === 1 ? 'setting' : 'settings'})
              </button>
              {showSources && (
                <ul className="mt-2 text-sm text-gray-700 space-y-1">
                  {response.matches.map((m) => (
                    <li key={m.node_id} className="flex gap-2">
                      <span className="font-mono text-xs text-gray-500">
                        {m.node_type}
                      </span>
                      <span>{m.natural_key}</span>
                      <span className="text-xs text-gray-400">
                        · {(m.similarity * 100).toFixed(1)}% match
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {ask.error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
          {(ask.error as any)?.response?.data?.message ||
            (ask.error as any)?.message ||
            'Query failed'}
        </div>
      )}
    </Card>
  );
}